<template>
  <div v-if="selectedAccount && !vatLoading" class="deposit-summary deposit__final-block">
    <!-- Сумма пополнения -->
    <div
      v-if="totals && activeAccount.tax_conditions[selectedAccount.service] === true"
      class="deposit-summary__item"
    >
      <div class="deposit-summary__title">{{ $t('deposit.top-up_amount') }}:</div>
      <div class="deposit-summary__value">
        {{ formatAmount(totals.baseDepositAmount || totals.finalAmount) }}
        {{ $t('currency.currency_symbol') }}
      </div>
    </div>

    <!-- НДС -->
    <div
      v-if="totals && activeAccount.tax_conditions[selectedAccount.service] === true"
      class="deposit-summary__item"
    >
      <div class="deposit-summary__title">{{ $t('vat') }}:</div>
      <div class="deposit-summary__value">
        {{ totals.nds || 0 }} {{ $t('currency.currency_symbol') }}
      </div>
    </div>

    <!-- Комиссия -->
    <div
      v-if="
        totals &&
        activeAccount.commissions_conditions &&
        activeAccount.commissions_conditions[selectedAccount.service]
      "
      class="deposit-summary__item"
    >
      <div class="deposit-summary__title">{{ $t('deposit.commission') }}:</div>
      <div class="deposit-summary__value">{{ totals.commissionPercent || 0 }} %</div>
    </div>

    <!-- Рекламный сбор -->
    <div v-if="totals && selectedAccount.service !== 'google'" class="deposit-summary__item">
      <div class="deposit-summary__title">
        {{ $t('deposit.advt_tax_display') }} ({{ activeAccount.advt_tax }}%):
      </div>
      <div class="deposit-summary__value">
        {{ totals.advtTaxAmount || 0 }} {{ $t('currency.currency_symbol') }}
      </div>
    </div>

    <!-- Итоговая сумма -->
    <div class="deposit-summary__total">
      <div class="deposit-summary__total-title">{{ $t('deposit.total_amount') }}</div>
      <div class="deposit-summary__total-value">
        {{ formatTotalAmount(totals) }}
        {{ getCurrencySymbol(totals?.serviceCurrency) }}
      </div>
    </div>

    <!-- Курс валюты -->
    <div
      v-if="
        totals &&
        totals.serviceCurrency &&
        totals.serviceCurrency !== totals.localCurrency &&
        totals.currencyRate
      "
      class="deposit-summary__rate"
    >
      {{ formatCurrencyRate(totals) }}
    </div>
  </div>
</template>

<script>
import { calculateDepositTotals } from '@/utils/forms/depositCalculations';
import { numberWithSpacesCeil, numberWithSpacesFixed } from '@/utils/forms/numberFormatting';
import { SITE_SETTINGS_STORE } from '../../store';

export default {
  name: 'DepositSummary',
  props: {
    selectedCampaigns: {
      type: Array,
      default: () => [],
    },
    accountAmount: {
      type: Number,
      default: 0,
    },
    selectedAccount: {
      type: Object,
      default: null,
    },
    activeAccount: {
      type: Object,
      required: true,
    },
    currencyRates: {
      type: Array,
      default: () => [],
    },
    includesAdvtTax: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    totals() {
      if (!this.selectedAccount || !this.activeAccount) {
        return null;
      }

      return calculateDepositTotals({
        selectedCampaigns: this.selectedCampaigns,
        accountAmount: this.accountAmount,
        selectedAccount: this.selectedAccount,
        activeAccount: this.activeAccount,
        currencyRates: this.currencyRates,
        vat: this.vat,
        includesAdvtTax: this.includesAdvtTax,
      });
    },

    vat() {
      return this.$store.getters[SITE_SETTINGS_STORE.GETTERS.GET_VAT];
    },

    vatLoading() {
      return this.$store.getters[SITE_SETTINGS_STORE.GETTERS.GET_VAT_LOADING];
    },
    serviceCurrencyIcon() {
      return {
        RUB: '₽',
        USD: '$',
        PLN: 'zł',
        EUR: '€',
      };
    },
  },
  methods: {
    formatAmount(amount) {
      if (!amount) return '0';
      return numberWithSpacesCeil(amount);
    },
    formatTotalAmount(totals) {
      if (!totals) return '0';

      let amount = 0;
      if (this.activeAccount.tax_conditions[this.selectedAccount.service] === true) {
        amount = totals.totalSum * totals.currencyRate;
      } else {
        amount = totals.finalAmountWithAdvtTax * totals.currencyRate;
      }

      return numberWithSpacesFixed(amount, 2);
    },
    getCurrencySymbol(currency) {
      if (!currency) return '₽';
      return this.serviceCurrencyIcon[currency] || '₽';
    },
    formatCurrencyRate(totals) {
      if (!totals || !totals.currencyRate || totals.serviceCurrency === totals.localCurrency) {
        return '';
      }

      const serviceSymbol = this.getCurrencySymbol(totals.serviceCurrency);
      const localSymbol = this.getCurrencySymbol(totals.localCurrency);
      const rate = (1 / totals.currencyRate).toFixed(4);

      return `1${serviceSymbol} = ${rate} ${localSymbol}`;
    },
  },
  created() {
    this.$store.dispatch(SITE_SETTINGS_STORE.ACTIONS.FETCH_VAT);
  },
};
</script>

<style scoped lang="scss">
@import '@/assets/styles/helpers/variables';
@import '@/assets/styles/helpers/mixin';

.deposit-summary {
  width: 100%;

  &__item {
    display: flex;
    align-items: center;
    margin-bottom: 1.5rem;
  }

  &__title {
    font-size: 1.8rem;
    font-weight: 400;
    line-height: 2.2rem;
    color: $grey1;
    margin-right: 1rem;
  }

  &__value {
    font-size: 1.8rem;
    font-weight: 700;
    line-height: 2.1rem;
    color: $basicBlack;
  }

  &__total {
    display: flex;
    align-items: center;
    margin-top: 3.6rem;

    &-title {
      font-size: 1.8rem;
      font-weight: 400;
      line-height: 2.2rem;
      color: $basicBlack;
      margin-right: 2rem;
    }

    &-value {
      font-size: 3rem;
      font-weight: 700;
      line-height: 3.5rem;
      color: $basicBlack;
    }
  }

  &__rate {
    color: #7f858d;
    font-size: 1.4rem;
  }
}
</style>
